/**
 * Reshapes the Judge.me v3 review widget header to the storefront's copy.
 *
 * Three things CSS alone cannot do:
 *   - fold the review count into the headline ("REVIEWS (315)")
 *   - draw a star row beside the average score, which Judge.me's "minimal"
 *     header prints as a bare number
 *   - relabel the custom-answer filter button as "Filter by rating"
 *
 * The widget is a Vue app that re-renders on sort, filter and pagination, so
 * every step re-runs from a MutationObserver and must be idempotent — writes
 * are skipped when the value is already correct, otherwise each write would
 * retrigger the observer.
 */
(() => {
  const SCALE = 5;

  /** First number in a string, or null. "Based on 315 reviews" -> 315. */
  const firstNumber = (text) => {
    const match = String(text || '').match(/\d+(\.\d+)?/);
    return match ? parseFloat(match[0]) : null;
  };

  /** Star row matching the product page's rating block, in half steps. */
  const buildStars = (rating) => {
    const row = document.createElement('span');
    row.className = 'fw-jm-stars';
    row.setAttribute('role', 'img');
    row.setAttribute('aria-label', `${rating} out of ${SCALE} stars`);

    const halves = Math.round(rating * 2);
    for (let i = 1; i <= SCALE; i += 1) {
      const full = i * 2;
      let state = 'empty';
      if (halves >= full) state = 'full';
      else if (halves === full - 1) state = 'half';

      const star = document.createElement('span');
      star.className = `fw-jm-star fw-jm-star--${state}`;
      star.setAttribute('aria-hidden', 'true');
      // Judge.me's own star glyph (private-use U+E000 in its JudgemeStar font),
      // so the header badge and the per-review rows are the same shape rather
      // than two fonts' takes on a star.
      star.textContent = '\uE000';
      row.appendChild(star);
    }
    return row;
  };

  const setText = (el, value) => {
    if (el && el.textContent.trim() !== value) el.textContent = value;
  };

  const decorateHeader = (header) => {
    const summary = header.querySelector('.jm-average-rating-display .jm-cluster');
    if (!summary) return;

    const paragraphs = summary.querySelectorAll(':scope > p');
    const rating = firstNumber(paragraphs[0] && paragraphs[0].textContent);
    const count = firstNumber(paragraphs[1] && paragraphs[1].textContent);

    // Headline carries the count; Judge.me's own count text is hidden in CSS.
    const title = header.querySelector('.jm-review-widget-minimal-header__title');
    if (title && count !== null) setText(title, `Reviews (${count})`);

    // Stars sit immediately after the score.
    if (rating !== null) {
      const existing = summary.querySelector('.fw-jm-stars');
      const wanted = String(rating);
      if (!existing) {
        const row = buildStars(rating);
        row.dataset.fwRating = wanted;
        paragraphs[0].after(row);
      } else if (existing.dataset.fwRating !== wanted) {
        const row = buildStars(rating);
        row.dataset.fwRating = wanted;
        existing.replaceWith(row);
      }
    }

    setText(header.querySelector('.jm-custom-answer-filter__text'), 'Filter by rating');
    buildSortMenu(header);
    buildTopicMenu(header);
    addAskQuestion(header);
    openSearch(header);
  };

  /**
   * Judge.me's compact filter row collapses the review search behind a
   * magnifier, and the input is only mounted once that toggle is pressed. The
   * reference shows a permanent field, so press it once on the widget's behalf
   * (the toggle itself is hidden in CSS).
   *
   * Safe against the observer: clicking causes one re-render, after which the
   * search bar exists and this returns early.
   */
  const openSearch = (header) => {
    if (header.querySelector('.jm-search-bar')) {
      const input = header.querySelector('.jm-search-bar__input');
      // Judge.me's own placeholder is just "Search".
      if (input && input.placeholder !== 'Search Reviews') input.placeholder = 'Search Reviews';
      return;
    }

    const toggle = header.querySelector('.jm-search-filter__toggle');
    if (toggle && !toggle.classList.contains('jm-search-filter__toggle--active')) toggle.click();
  };

  /**
   * "Ask a question" beside "Write a review".
   *
   * Deliberately not Judge.me's Q&A button — this opens the theme's own contact
   * dialog (snippets/fw-ask-question.liquid). Judge.me owns this header and
   * re-renders it on sort, filter and pagination, wiping anything added to it,
   * so the observer puts the button back each time.
   */
  const addAskQuestion = (header) => {
    const row = header.querySelector('.jm-action-buttons > .jm-stack');
    const dialog = document.getElementById('fw-askq-dialog');
    if (!row || !dialog || row.querySelector('.fw-askq-trigger')) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'fw-askq-trigger';
    button.textContent = 'Ask a question';
    button.addEventListener('click', () => dialog.showModal());
    row.appendChild(button);
  };

  /**
   * Write-a-review modal: score dropdown, heading, and the collapsed flow.
   *
   * Judge.me drives its score from a row of star buttons across a separate
   * intro step. With the wizard flattened to one form, the dropdown stands in
   * for that row — picking a value clicks the matching star, so Judge.me's own
   * state and validation still run.
   */
  const decorateReviewModal = () => {
    const modal = document.querySelector('.jdgm-write-review-modal');
    const content = modal?.querySelector('.jdgm-write-review-modal__content');
    if (!content) return;

    if (!content.querySelector('.fw-review-heading')) {
      const heading = document.createElement('p');
      heading.className = 'fw-review-heading';
      heading.textContent = 'Write a review';
      content.appendChild(heading);
    }

    relabel(modal);
    addScoreSelect(modal, content);
    wireSubmit(modal);
    localisePolicyLinks(modal);
  };

  /**
   * Judge.me's consent line links to judge.me/terms and judge.me/privacy —
   * its own policies, not the shop's. Repoint them at the store's pages.
   *
   * Matched on the href rather than the link text, which Judge.me translates.
   */
  const POLICY_LINKS = [
    { match: 'judge.me/terms', attribute: 'data-fw-terms-url' },
    { match: 'judge.me/privacy', attribute: 'data-fw-privacy-url' },
  ];

  const localisePolicyLinks = (modal) => {
    const script = document.querySelector('script[data-fw-privacy-url]');
    if (!script) return;

    POLICY_LINKS.forEach(({ match, attribute }) => {
      const url = script.getAttribute(attribute);
      if (!url) return;

      modal.querySelectorAll(`a[href*="${match}"]`).forEach((link) => {
        link.href = url;
        // nofollow was for an external site; these are the shop's own pages.
        link.rel = 'noopener';
      });
    });
  };

  /**
   * The wizard's step state survives the flattening: its button still says
   * "Next" and each press validates one page and advances to the following
   * one. With every field on screen at once that would mean pressing Next
   * three times, so the press is carried through the remaining steps here.
   *
   * Bounded and guarded: it stops on a validation error, on reaching the
   * thank-you step, or when a press fails to change the current page.
   */
  const wireSubmit = (modal) => {
    const next = modal.querySelector('.jdgm-write-review-modal__nav-btn-next');
    if (!next) return;

    setText(next, 'Submit review');

    if (next.dataset.fwDriven === 'true') return;
    next.dataset.fwDriven = 'true';
    next.addEventListener('click', () => advanceSteps(modal, 0));
  };

  const currentPage = (modal) =>
    modal.querySelector('.jdgm-write-review-modal__page.current-page')?.className || '';

  const hasFieldError = (modal) =>
    [...modal.querySelectorAll('.jdgm-write-review-modal__field-error')].some((error) =>
      error.textContent.trim()
    );

  const advanceSteps = (modal, depth, previousPage) => {
    if (depth > 4) return;

    setTimeout(() => {
      // Submitted: Judge.me has swapped in its thank-you step.
      if (modal.querySelector('.jdgm-write-review-modal__section--page--thank-you.current-section')) {
        return;
      }
      // A field failed validation — leave the message on screen.
      if (hasFieldError(modal)) return;

      const page = currentPage(modal);
      // The press did not move the wizard on, so pressing again will not help.
      if (previousPage !== undefined && page === previousPage) return;

      modal.querySelector('.jdgm-write-review-modal__nav-btn-next')?.click();
      advanceSteps(modal, depth + 1, page);
    }, 700);
  };

  /** Judge.me's own field wording, in the storefront's voice. */
  const LABELS = {
    reviewer_name: 'Your name',
    reviewer_email: 'Your email',
    review_title: 'Review title',
    review_body: 'Your review',
  };

  const relabel = (modal) => {
    const score = modal.querySelector('.fw-review-score__label');
    if (score) setText(score, 'Review rating');

    Object.entries(LABELS).forEach(([name, text]) => {
      const field = modal.querySelector(`[name="${name}"]`);
      const label = field?.closest('.jdgm-write-review-modal__field-group')
        ?.querySelector('.jdgm-write-review-modal__field-label');
      if (label) setText(label, text);
    });
  };

  const addScoreSelect = (modal, content) => {
    if (content.querySelector('.fw-review-score')) return;

    // The star row on the details page is the one still in the flattened form.
    const stars = [
      ...modal.querySelectorAll(
        '.jdgm-write-review-modal__page--review-details .jdgm-star'
      ),
    ];
    if (!stars.length) return;

    const group = document.createElement('div');
    group.className = 'fw-review-score';

    const label = document.createElement('label');
    label.className = 'fw-review-score__label';
    label.setAttribute('for', 'fw-review-score');
    label.textContent = 'Review rating';

    const select = document.createElement('select');
    select.className = 'fw-review-score__select';
    select.id = 'fw-review-score';

    const blank = document.createElement('option');
    blank.value = '';
    blank.textContent = 'Select a rating';
    select.appendChild(blank);

    for (let value = 5; value >= 1; value -= 1) {
      const option = document.createElement('option');
      option.value = String(value);
      option.textContent = `${value} star${value === 1 ? '' : 's'}`;
      select.appendChild(option);
    }

    select.addEventListener('change', () => {
      const value = parseInt(select.value, 10);
      if (value >= 1 && value <= stars.length) stars[value - 1].click();
    });

    group.append(label, select);
    content.appendChild(group);

    // Reflect a score already set (Judge.me remembers it across steps).
    const current = modal.querySelector('input[name="score"]')?.value;
    if (current) select.value = current;
  };

  /** Close controls for the dialog, wired once. */
  const wireAskDialog = () => {
    const dialog = document.getElementById('fw-askq-dialog');
    if (!dialog) return;

    dialog.querySelector('.fw-askq-dialog__close')?.addEventListener('click', () => dialog.close());

    // Clicking the backdrop lands on the dialog itself, never on its contents.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    // Shopify reloads the page after a contact-form post, so reopen the dialog
    // to show the success message rather than dropping the shopper back to the
    // page with no sign anything happened. Keyed off the rendered success
    // element: `form.posted_successfully?` is only in scope inside the
    // `{% form %}` block, so it cannot be read from the dialog tag itself.
    if (dialog.querySelector('.fw-askq-form__success')) dialog.showModal();
  };

  /* ---- Shared dropdown plumbing ----
     A native <select> renders its option list through the OS, which no CSS can
     reach — so the sort control is mirrored into a real menu that writes back
     to the (now hidden) select. The topic filter mirrors Judge.me's keyword
     chips the same way. Both keep Judge.me as the source of truth: every click
     is forwarded to the element it already owns.

     Menus are rebuilt only when their source data changes, tracked with a
     signature on the wrapper. Rebuilding unconditionally would rewrite the DOM
     on every observer pass. */
  const closeAllMenus = (except) => {
    document.querySelectorAll('.fw-menu[data-open="true"]').forEach((menu) => {
      if (menu === except) return;
      menu.dataset.open = 'false';
      menu.querySelector('.fw-menu__trigger')?.setAttribute('aria-expanded', 'false');
    });
  };

  const toggleMenu = (menu) => {
    const open = menu.dataset.open !== 'true';
    closeAllMenus(open ? menu : null);
    menu.dataset.open = String(open);
    menu.querySelector('.fw-menu__trigger')?.setAttribute('aria-expanded', String(open));
  };

  const makeMenu = (className) => {
    const menu = document.createElement('div');
    menu.className = `fw-menu ${className}`;
    menu.dataset.open = 'false';

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'fw-menu__trigger';
    trigger.setAttribute('aria-haspopup', 'true');
    trigger.setAttribute('aria-expanded', 'false');

    const label = document.createElement('span');
    label.className = 'fw-menu__label';

    const chevron = document.createElement('span');
    chevron.className = 'fw-menu__chevron';
    chevron.setAttribute('aria-hidden', 'true');

    trigger.append(label, chevron);

    const panel = document.createElement('div');
    panel.className = 'fw-menu__panel';
    panel.setAttribute('role', 'menu');

    trigger.addEventListener('click', (event) => {
      event.stopPropagation();
      toggleMenu(menu);
    });

    menu.append(trigger, panel);
    return { menu, label, panel };
  };

  /**
   * Sort: a menu over Judge.me's own <select>. Selecting an item sets the
   * select's value and fires `change`, which is the event Judge.me already
   * listens to — so sorting keeps running through its code, not ours.
   */
  const buildSortMenu = (header) => {
    const wrapper = header.querySelector('.jm-sort-filter');
    const select = wrapper?.querySelector('select');
    if (!wrapper || !select) return;

    const signature = [...select.options].map((option) => option.value).join('|');
    let menu = wrapper.querySelector('.fw-menu--sort');

    if (menu && menu.dataset.fwSignature === signature) {
      syncSortLabel(menu, select);
      return;
    }

    menu?.remove();
    const built = makeMenu('fw-menu--sort');
    menu = built.menu;
    menu.dataset.fwSignature = signature;

    [...select.options].forEach((option) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'fw-menu__item';
      item.setAttribute('role', 'menuitem');
      item.dataset.value = option.value;
      item.textContent = option.text;
      item.addEventListener('click', () => {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        toggleMenu(menu);
      });
      built.panel.appendChild(item);
    });

    wrapper.appendChild(menu);
    syncSortLabel(menu, select);
  };

  const syncSortLabel = (menu, select) => {
    const current = select.options[select.selectedIndex];
    if (!current) return;

    const label = menu.querySelector('.fw-menu__label');
    if (label && label.textContent !== current.text) label.textContent = current.text;

    menu.querySelectorAll('.fw-menu__item').forEach((item) => {
      const selected = item.dataset.value === select.value;
      if ((item.dataset.selected === 'true') !== selected) {
        item.dataset.selected = String(selected);
      }
    });
  };

  /**
   * Filter by topics: a checkbox menu mirroring Judge.me's AI keyword chips.
   * Each row forwards its click to the chip it mirrors, so the actual filtering
   * stays Judge.me's. Renders nothing until Judge.me has extracted keywords,
   * which needs a body of reviews to work from.
   */
  const buildTopicMenu = (header) => {
    const trigger = header.querySelector('.jm-custom-answer-filter');
    const chips = [...header.querySelectorAll('.jm-keyword-list .jm-keyword-sentiment')];
    if (!trigger) return;

    const host = trigger.parentElement;
    let menu = host.querySelector('.fw-menu--topics');

    // Nothing to filter by: drop any stale menu and leave Judge.me's button.
    if (!chips.length) {
      menu?.remove();
      trigger.style.removeProperty('display');
      return;
    }

    const labelOf = (chip) =>
      (chip.querySelector('.jm-keyword-sentiment__text') || chip).textContent.trim();
    const signature = chips.map(labelOf).join('|');

    if (!menu || menu.dataset.fwSignature !== signature) {
      menu?.remove();
      const built = makeMenu('fw-menu--topics');
      menu = built.menu;
      menu.dataset.fwSignature = signature;
      built.label.textContent = 'Filter by rating';

      chips.forEach((chip) => {
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'fw-menu__item fw-menu__item--check';
        row.setAttribute('role', 'menuitemcheckbox');
        row.dataset.keyword = labelOf(chip);

        const box = document.createElement('span');
        box.className = 'fw-menu__box';
        box.setAttribute('aria-hidden', 'true');

        const text = document.createElement('span');
        text.textContent = labelOf(chip);

        row.append(box, text);
        row.addEventListener('click', () => {
          chip.click();
          // Judge.me flags the active chip with a class, which is an attribute
          // change — re-read it directly rather than waiting on the observer.
          requestAnimationFrame(() => syncTopicChecks(menu, chips, labelOf));
        });
        built.panel.appendChild(row);
      });

      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'fw-menu__clear';
      clear.textContent = 'Clear all';
      clear.addEventListener('click', () => {
        // Judge.me holds a single selected keyword; clicking it deselects.
        header
          .querySelectorAll('.jm-keyword-list .jm-keyword-sentiment--selected')
          .forEach((chip) => chip.click());
        requestAnimationFrame(() => syncTopicChecks(menu, chips, labelOf));
      });
      built.panel.appendChild(clear);

      // Judge.me's own button is replaced, not restyled: its panel is a
      // different component from the keyword row we are mirroring.
      trigger.style.display = 'none';
      host.insertBefore(menu, trigger);
    }

    syncTopicChecks(menu, chips, labelOf);
  };

  const syncTopicChecks = (menu, chips, labelOf) => {
    chips.forEach((chip) => {
      const row = menu.querySelector(`[data-keyword="${CSS.escape(labelOf(chip))}"]`);
      if (!row) return;
      const on = chip.classList.contains('jm-keyword-sentiment--selected');
      if ((row.dataset.checked === 'true') !== on) row.dataset.checked = String(on);
      if (row.getAttribute('aria-checked') !== String(on)) {
        row.setAttribute('aria-checked', String(on));
      }
    });
  };

  /**
   * Classic-widget timestamps (the all-reviews page) ship as a spinner with
   * the real value only in the datetime attribute — render it from there.
   */
  const fillClassicDates = () => {
    document.querySelectorAll('.fw-allrev .jdgm-rev__timestamp').forEach((ts) => {
      if (ts.dataset.fwDated === 'true' || ts.textContent.trim()) return;
      const raw = ts.getAttribute('datetime');
      if (!raw) return;
      const date = new Date(raw);
      if (Number.isNaN(date.getTime())) return;
      ts.textContent = date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
      ts.classList.remove('jdgm-spinner');
      ts.dataset.fwDated = 'true';
    });
  };

  /**
   * Per-review tweaks:
   *   - move the star rating to sit between the reviewer name and the date
   *   - add a "Verified" badge to every review. The old store's reviews were
   *     imported from WooCommerce, so Judge.me can't order-match them to a
   *     Shopify customer and never draws its own badge — but they are genuine
   *     past customers, so surface the badge anyway.
   * Judge.me re-renders each item on sort/filter/paginate, so this is idempotent
   * (guards) and re-runs from the same observer as everything else.
   */
  const decorateReviewItems = () => {
    document.querySelectorAll('.jm-review-item').forEach((item) => {
      const details = item.querySelector('.jm-reviewer-info__details');
      if (!details) return;
      const clusters = [...details.children];
      const nameCluster = clusters.find((c) => c.querySelector('.jm-reviewer-info__name'));
      if (!nameCluster) return;
      const dateCluster = clusters.find((c) => c !== nameCluster);

      // Rating between name and date.
      const star = item.querySelector('.jm-star-rating');
      if (star && star.parentElement !== details) {
        details.insertBefore(star, dateCluster || null);
      }

      // Verified badge on every review.
      if (!nameCluster.querySelector('.fw-jm-verified')) {
        const badge = document.createElement('span');
        badge.className = 'jm-verified-badge fw-jm-verified';
        badge.textContent = 'Verified Buyer';
        nameCluster.appendChild(badge);
      }
    });
  };

  const run = () => {
    document.querySelectorAll('.jm-review-widget-minimal-header').forEach(decorateHeader);
    decorateReviewItems();
    decorateReviewModal();
    fillClassicDates();
    // All-reviews page: match the product page's link wording.
    setText(document.querySelector('.fw-allrev .jdgm-write-rev-link'), 'Write a review');
  };

  const start = () => {
    wireAskDialog();

    document.addEventListener('click', () => closeAllMenus(null));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeAllMenus(null);
    });

    run();

    // Scoped to the widget, not document.body: the measuring span above lives
    // in the body, and observing the body would make its writes retrigger this
    // callback in an unbreakable loop.
    const observer = new MutationObserver(run);
    const roots = document.querySelectorAll('.jdgm-widget');
    if (roots.length) {
      // `class` is watched as well as childList: Judge.me marks the selected
      // keyword chip by toggling a class, which childList alone never sees.
      const options = {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      };
      roots.forEach((root) => observer.observe(root, options));
      // The write-review modal is appended to <body>, outside the widget root.
      observer.observe(document.body, { childList: true });
    } else {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* The Judge.me widget focuses its review-search input while initialising
   (and again as content streams in), which makes the browser scroll the
   freshly loaded page down to the reviews section. Until the visitor
   genuinely interacts, swallow programmatic focus() calls on those inputs
   so the page stays where it loaded. */
(function () {
  let userActed = false;
  const markActed = () => { userActed = true; };
  ['pointerdown', 'keydown', 'wheel', 'touchstart'].forEach((t) =>
    document.addEventListener(t, markActed, { capture: true, passive: true })
  );
  const guarded = '.jm-search-bar__input, [class*="jm-"] input[type="search"]';
  const origFocus = HTMLElement.prototype.focus;
  HTMLElement.prototype.focus = function (...args) {
    if (!userActed && this.matches && this.matches(guarded)) return;
    return origFocus.apply(this, args);
  };
})();
